import { T } from '../styles/tokens';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../api/client';
import { activeProject } from '../stores/project.store';
import { SkeletonBar, Button } from '../components/ui';

export function ActionDetailPage({ actionId }: { actionId?: string; path?: string }) {
  const [data, setData] = useState<any>(null);
  const [runners, setRunners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selectedRunner, setSelectedRunner] = useState('auto');
  const [params, setParams] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!actionId) return;
    
    Promise.all([
      api.get<any>(`/actions/${actionId}`),
      api.get<any>('/runner-v3/list'),
    ])
      .then(([actionRes, runnersRes]: any[]) => {
        setData(actionRes);
        const allRunners = runnersRes?.data?.runners || runnersRes?.runners || [];
        const now = Date.now();
        const onlineRunners = allRunners.filter((r: any) => {
          if (r.status === 'online' && r.last_seen_at) {
            return (now - new Date(r.last_seen_at).getTime()) < 45_000;
          }
          return false;
        });
        setRunners(onlineRunners);
        
        // Initialize params from input_schema
        const schema = actionRes?.action?.input_schema || {};
        const initial: Record<string, string> = {};
        if (schema.properties) {
          for (const [key, prop] of Object.entries(schema.properties as Record<string, any>)) {
            initial[key] = prop.default?.toString() ?? '';
          }
        }
        setParams(initial);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [actionId]);

  const executeAction = async () => {
    const projectId = activeProject.value?.id;
    setRunning(true);
    setError('');
    try {
      const body: any = {
        action_id: actionId,
        ...(projectId && { project_id: projectId }),
        params,
      };
      if (selectedRunner !== 'auto') body.runner_id = selectedRunner;
      const res = await api.post<any>(`/actions/${actionId}/run`, body);
      route(`/runs/${res.run_id}`);
    } catch (e: any) {
      const msg = e?.message || 'Erro ao executar ação';
      const code = e?.code || '';
      if (code === 'ORCH_INTEGRATION_MISSING' || msg.includes('Integrações faltando')) {
 setError(` ${msg}`);
      } else if (code === 'ORCH_RUNNER_OFFLINE' || msg.includes('Runner')) {
 setError(` ${msg}`);
      } else {
        setError(e?.details ? `${msg}: ${JSON.stringify(e.details)}` : msg);
      }
      setRunning(false);
    }
  };

  if (loading) return (<div style={{ padding: 20 }}><SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} /><SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="70%" height={14} /></div>);
  if (!data?.action) return <div class="page-empty">Ação não encontrada</div>;

  const { action, readiness } = data;
  const inputSchema = action.input_schema || {};
  const properties = inputSchema.properties || {};
  const requiredFields = inputSchema.required || [];
  const requiredIntegrations: any[] = Array.isArray(action.required_integrations) ? action.required_integrations : [];
  const missingIntegrations = readiness?.missing_integrations ?? [];
  const jobGraph: any[] = Array.isArray(action.job_graph) ? action.job_graph : [];

  return (
    <div style={{ paddingBottom: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: `linear-gradient(135deg, ${T.primaryBg}, ${T.accentBg})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 20, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{action.name}</h1>
          {action.description && <p style={{ margin: '4px 0 0', color: T.textMuted, fontSize: 13 }}>{action.description}</p>}
        </div>
      </div>

      {/* Readiness panel */}
      <div class="card" style={{ marginBottom: 20, border: readiness?.can_execute ? '1px solid var(--success)' : `1px solid ${T.warning}` }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>
 {readiness?.can_execute ? 'Pronto para executar' : 'Configuração necessária'}
        </h3>

        {/* Integration checklist */}
        {requiredIntegrations.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <strong style={{ fontSize: 13, color: T.textMuted }}>Integrações obrigatórias:</strong>
            <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
              {requiredIntegrations.map((integ: any) => {
                const provider = typeof integ === 'string' ? integ : integ.provider;
                const label = typeof integ === 'string' ? integ : (integ.label || integ.provider);
                const isMissing = missingIntegrations.includes(provider);
                return (
                  <div key={provider} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
 <span style={{ fontSize: 16 }}>{isMissing ? '' : ''}</span>
                    <span style={{ fontWeight: 500 }}>{label}</span>
                    {isMissing && (
                      <a href="/integrations" style={{ fontSize: 12, color: T.accent }}>
                        Configurar →
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Runner status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
 <span style={{ fontSize: 16 }}>{readiness?.runner_online ? '' : ''}</span>
          <span style={{ fontWeight: 500 }}>Executor OpenClaw</span>
          {readiness?.runner_online ? (
            <span class="badge badge-success" style={{ fontSize: 11 }}>Online</span>
          ) : (
            <>
              <span class="badge badge-error" style={{ fontSize: 11 }}>
                {readiness?.runner_status || 'Não registrado'}
              </span>
              <a href="/connect" style={{ fontSize: 12, color: T.accent }}>
                Conectar →
              </a>
            </>
          )}
        </div>
      </div>

      {/* Required AI Models */}
      {(() => {
        const models = new Map<string, number>();
        // From job_graph steps
        for (const step of jobGraph) {
          const m = step.config?.model || step.config?.system_prompt ? 'haiku' : null;
          const model = step.config?.model || m;
          if (model) models.set(model, (models.get(model) || 0) + 1);
        }
        // From LLM config (single prompt mode)
        if (jobGraph.length === 0 && action.execution_mode === 'llm') {
          try {
            const meta = typeof action.metadata === 'string' ? JSON.parse(action.metadata) : action.metadata || {};
            const m = meta.llm_config?.model || 'haiku';
            models.set(m, 1);
          } catch {}
        }
        if (models.size === 0) return null;

        const MODEL_INFO: Record<string, { name: string; icon: string; color: string }> = {
 haiku: { name: 'Claude Haiku 4.5', icon: '', color: T.success },
 sonnet: { name: 'Claude Sonnet 4.5', icon: '', color: T.primary },
 opus: { name: 'Claude Opus 4.6', icon: '', color: T.purple },
        };

        const totalSteps = Array.from(models.values()).reduce((a, b) => a + b, 0);

        return (
          <div class="card" style={{ marginBottom: 20 }}>
 <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Modelos de IA Necessários</h3>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: T.textMuted }}>
              Sua OpenClaw precisa ter acesso a estes modelos para executar esta ação.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {Array.from(models.entries()).map(([model, count]) => {
 const info = MODEL_INFO[model] || { name: model, icon: '', color: T.textSecondary };
                return (
                  <div key={model} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', borderRadius: 10,
                    background: info.color + '10',
                    border: `1px solid ${info.color}30`,
                    flex: '1 1 200px',
                  }}>
                    <span style={{ fontSize: 24 }}>{info.icon}</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: info.color }}>{info.name}</div>
                      <div style={{ fontSize: 11, color: T.textMuted }}>
                        {count} {count === 1 ? 'etapa' : 'etapas'} de {totalSteps}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Execution mode badge */}
      {action.execution_mode === 'llm' && jobGraph.length === 0 && (
        <div class="card" style={{ marginBottom: 20 }}>
 <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Modo de Execução: IA Direta</h3>
          <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>
            Esta ação usa um prompt de IA. O runner executa a chamada automaticamente.
          </p>
        </div>
      )}

      {/* Job graph preview */}
      {jobGraph.length > 0 && (
        <div class="card" style={{ marginBottom: 20 }}>
 <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>Pipeline ({jobGraph.length} {jobGraph.length === 1 ? 'etapa' : 'etapas'})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {jobGraph.map((step: any, i: number) => (
              <div key={step.key} style={{ display: 'flex', gap: 12, paddingBottom: i < jobGraph.length - 1 ? 16 : 0, position: 'relative' }}>
                {/* Timeline line */}
                {i < jobGraph.length - 1 && (
                  <div style={{
                    position: 'absolute', left: 13, top: 28, bottom: 0,
                    width: 2, background: T.border,
                  }} />
                )}
                {/* Number circle */}
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: T.primary, color: T.text, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, flexShrink: 0, position: 'relative', zIndex: 1,
                }}>
                  {i + 1}
                </div>
                {/* Content */}
                <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{step.name}</div>
                  <div style={{ fontSize: 12, color: T.textMuted, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontFamily: 'monospace', background: T.bgHover, padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>
 {step.type === 'llm_prompt' || step.type === 'ai_prompt' ? 'IA' : step.type}
                    </span>
                    {step.config?.model && (
                      <span style={{
                        padding: '1px 6px', borderRadius: 4, fontSize: 11,
                        background: step.config.model === 'opus' ? '#a855f710' : step.config.model === 'sonnet' ? '#3b82f610' : '#22c55e10',
                        color: step.config.model === 'opus' ? T.purple : step.config.model === 'sonnet' ? T.primary : T.success,
                        border: `1px solid ${step.config.model === 'opus' ? `${T.purple}30` : step.config.model === 'sonnet' ? `${T.primary}30` : `${T.success}30`}`,
                      }}>
 {step.config.model === 'opus' ? 'Opus' : step.config.model === 'sonnet' ? 'Sonnet' : 'Haiku'}
                      </span>
                    )}
                    {step.depends_on?.length > 0 && (
                      <span>← {step.depends_on.join(', ')}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dynamic Form */}
      {Object.keys(properties).length > 0 && (
        <div class="card" style={{ marginBottom: 20 }}>
 <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>Parâmetros</h3>
          {Object.entries(properties).map(([key, prop]: [string, any]) => (
            <div style={{ marginBottom: 12 }} key={key}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                {prop.title || key}
                {requiredFields.includes(key) && <span style={{ color: T.danger }}> *</span>}
              </label>
              {prop.description && <span style={{ fontSize: 11, color: T.textMuted, display: 'block', marginBottom: 4 }}>{prop.description}</span>}
              {prop.enum ? (
                <select class="select" value={params[key] || ''} onChange={(e: any) => setParams({ ...params, [key]: e.target.value })}>
                  <option value="">Selecione...</option>
                  {prop.enum.map((v: string) => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : prop.type === 'boolean' ? (
                <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={params[key] === 'true'} onChange={(e: any) => setParams({ ...params, [key]: e.target.checked ? 'true' : 'false' })} />
                  {prop.title || key}
                </label>
              ) : (
                <input
                  class="input"
                  type={prop.type === 'number' || prop.type === 'integer' ? 'number' : 'text'}
                  value={params[key] || ''}
                  placeholder={prop.default?.toString() || ''}
                  onInput={(e: any) => setParams({ ...params, [key]: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Runner Selector */}
      <div class="card" style={{ marginBottom: 20 }}>
 <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Executor</h3>
        <select class="select" value={selectedRunner} onChange={(e: any) => setSelectedRunner(e.target.value)}>
 <option value="auto">Automático (próximo runner disponível)</option>
          {runners.map((r: any) => (
            <option key={r.id} value={r.id}>
 {r.status === 'online' ? '' : ''} {r.name} ({r.runner_mode})
            </option>
          ))}
        </select>
      </div>

 {error && <div class="error-banner" style={{ marginBottom: 16 }}> {error}</div>}

      {!readiness?.can_execute && (
        <p style={{ textAlign: 'center', fontSize: 13, color: T.textMuted, marginBottom: 8 }}>
          Configure as integrações e conecte um executor para rodar.
        </p>
      )}

      {/* Fixed bottom action button */}
      <div class="action-fixed-bar">
        <Button variant="ghost"
          class="btn btn-primary"
          style={{ width: '100%', padding: '14px 24px', fontSize: 16 }}
          onClick={executeAction}
          disabled={running || !readiness?.can_execute}
        >
 {running ? 'Iniciando...' : 'Rodar Ação'}
        </Button>
        {action.estimated_duration_sec && (
          <p style={{ textAlign: 'center', fontSize: 11, color: T.textMuted, margin: '6px 0 0' }}>
 ~{Math.ceil(action.estimated_duration_sec / 60)} min
          </p>
        )}
      </div>
    </div>
  );
}
